import { service } from '@ember/service';
import { htmlSafe } from '@ember/template';
import { cached } from '@glimmer/tracking';

import moment from 'moment';

import type { CreateRecordProperties, ImageCropData, RecordData, Service } from 'webapp-types';
import { ModelName } from 'webapp/enums/enums';
import { getOrderFromPosition, getPositionFromOrder, uniq } from 'webapp/utils/common';

import type Board from 'webapp/models-custom/board';
import BufferedModel from 'webapp/models-custom/buffer/buffered-model';
import type DateRange from 'webapp/models-custom/date-range';
import type Report from 'webapp/models-custom/report';
import type ReportSlideFragment from 'webapp/models-custom/report-slide-fragment';
import type ReportSlideRecord from 'webapp/models/report-slide';
import { DEFAULT_SLIDE_TITLE } from 'webapp/models/report-slide';
import type UserRecord from 'webapp/models/user';

import { Severity } from 'webapp/services/logger';
import ModelManagerReportSlideFragmentsService from 'webapp/services/model-manager/report-slide-fragments';

export default class ReportSlide extends BufferedModel<
	ReportSlideRecord,
	RecordData.ReportSlide.Serialized
> {
	@service('account') declare accountService: Service.Account;
	@service('logger') declare loggerService: Service.Logger;
	@service('request') declare requestService: Service.Request;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('model-manager/boards') declare mmBoardsService: Service.ModelManager.Boards;
	@service('model-manager/board-previews')
	declare mmBoardPreviewsService: Service.ModelManager.BoardPreviews;
	@service('model-manager/reports') declare mmReportsService: Service.ModelManager.Reports;
	@service('model-manager/report-slides')
	declare mmReportSlidesService: Service.ModelManager.ReportSlides;
	@service('model-manager/report-slide-fragments')
	declare mmReportSlideFragmentsService: Service.ModelManager.ReportSlideFragments;
	@service('model-manager/users') declare mmUsersService: Service.ModelManager.Users;
	@service('model-manager/blocks') declare mmBlocksService: Service.ModelManager.Blocks;

	// ------- props from / to ED record ------

	get id(): string | null {
		return this.model.id;
	}

	get reportId(): number {
		return this.getBuffered('reportId');
	}

	set reportId(value: number) {
		this.setBuffered('reportId', value);
	}

	get order(): number {
		return this.getBuffered('order');
	}

	set order(value: number) {
		this.setBuffered('order', value);
	}

	get title(): string {
		return this.getBuffered('title');
	}

	set title(value: string) {
		this.setBuffered('title', value);
	}

	get description(): RecordData.ReportSlide.Description {
		return this.getBuffered('description');
	}

	set description(value: RecordData.ReportSlide.Description) {
		this.setBuffered('description', value);
	}

	get image(): string {
		return this.getBuffered('image');
	}

	get color(): string {
		return this.getBuffered('color');
	}

	set color(value: string) {
		this.setBuffered('color', value);
	}

	get themeId(): number {
		return this.getBuffered('themeId');
	}

	set themeId(value: number) {
		this.setBuffered('themeId', value);
	}

	get logo(): string {
		return this.getBuffered('logo');
	}

	set logo(value: string) {
		this.setBuffered('logo', value);
	}

	get logoOriginal(): string {
		return this.getBuffered('logoOriginal');
	}

	set logoOriginal(value: string) {
		this.setBuffered('logoOriginal', value);
	}

	get logoCrop(): ImageCropData | null {
		return this.getBuffered('logoCrop');
	}

	set logoCrop(value: ImageCropData | null) {
		this.setBuffered('logoCrop', value);
	}

	get bg(): string {
		return this.getBuffered('bg');
	}

	set bg(value: string) {
		this.setBuffered('bg', value);
	}

	get bgOriginal(): string {
		return this.getBuffered('bgOriginal');
	}

	set bgOriginal(value: string) {
		this.setBuffered('bgOriginal', value);
	}

	get bgCrop(): ImageCropData | null {
		return this.getBuffered('bgCrop');
	}

	set bgCrop(value: ImageCropData | null) {
		this.setBuffered('bgCrop', value);
	}

	get bgOpacity(): number {
		return this.getBuffered('bgOpacity');
	}

	set bgOpacity(value: number) {
		this.setBuffered('bgOpacity', value);
	}

	// ------- other props ------

	get isTypeBoard(): boolean {
		return !!this.fragments[0]?.isTypeBoard;
	}

	@cached
	get isTypeCover(): boolean {
		// very wonky logic used only to force centered title/text fragments
		// don't use it for anything else, pretty please
		const fragments = this.fragments,
			imageFragment = fragments.find((item) => item.isTypeImage),
			titleFragment = fragments.find((item) => item.isTypeTitle),
			textFragment = fragments.find((item) => item.isTypeStoryText);
		// assume it's cover even if the text does not exist

		// exclude newly created fragment for exeption when creating new fragment on pageless when only cover slide is present
		const existingFragmentsLength = fragments.filter((f) => !f.isNewlyCreated).length;

		return (
			imageFragment?.order === getOrderFromPosition(1, 1) &&
			titleFragment?.order === getOrderFromPosition(2, 1) &&
			(!textFragment || textFragment?.order === getOrderFromPosition(3, 1)) &&
			existingFragmentsLength === (textFragment ? 3 : 2)
		);
	}

	@cached
	get isTypeCoverPageless(): boolean {
		// also wonky logic adjusted to properly handle when to show and when not to show first image fragment on pageless
		const isFirstSlide = this.report?.slides[0]?.id === this.id;
		if (!isFirstSlide || !this.report?.isPageless) {
			return false;
		}

		const fragments = this.fragments,
			imageFragment = fragments.find((item) => item.isTypeImage),
			titleFragment = fragments.find((item) => item.isTypeTitle),
			row1Fragments = fragments.filter((f) => f.position.row === 1);

		return (
			imageFragment?.order === getOrderFromPosition(1, 1) &&
			titleFragment?.position.row === 2 &&
			row1Fragments.length === 1
		);
	}

	get report(): Report | null {
		let report;
		if (this.reportId) {
			report = this.mmReportsService.findModel(String(this.reportId));
			if (!report) {
				this.loggerService.logError(
					'Missing report for report slide',
					{
						reportSlideId: this.id,
						reportId: this.reportId,
					},
					Severity.WARNING,
				);
			}
		}
		return report || null;
	}

	get board(): Board | null {
		// ignoring preview - use this for everything except data
		return this.boardFragment?.board || null;
	}

	get boardOrPreview(): Board | null {
		// use this for data
		return this.boardFragment?.boardOrPreview || null;
	}

	get bgTheme(): string {
		let bgTheme;
		if (this.isTypeBoard) {
			bgTheme = this.board?.bgTheme || 'light';
		} else {
			bgTheme = this.mmBoardsService.getBgTheme(this.color, this.themeId);
		}
		return bgTheme;
	}

	get colorConfig() {
		return this.mmBoardsService.getColorConfig(this.color, this.themeId);
	}

	get fallbackImage(): string {
		if (this.isTypeBoard) {
			return '';
		} else {
			return this.fallbackImageAll;
		}
	}

	get fallbackImageAll(): string {
		return this.mmReportSlidesService.getSlideFallbackImage(this.bgTheme);
	}

	get colorStyle() {
		return htmlSafe(this.colorGradient);
	}

	get bgStyle() {
		let style = '';
		if (this.model.bg) {
			style = `background-image:url("${this.model.bg}");`;
		}
		style += `opacity:${this.model.bgOpacity};`;
		return htmlSafe(style);
	}

	get shownLogo(): string {
		return this.logo || this.accountService.boardLogo;
	}

	get shownImage(): string {
		return this.image || this.boardOrPreview?.image || '';
	}

	get shownColor(): string {
		return this.board?.color || this.color || '';
	}

	get colorGradient(): string {
		return this.mmBoardsService.getColorGradient(this.shownColor);
	}

	get shownTitle() {
		const title = this.title && this.title !== DEFAULT_SLIDE_TITLE ? this.title : null;
		return title || this.boardOrPreview?.name;
	}

	// ----- description props ------

	get descriptionContent(): string {
		return this.description?.content || '';
	}

	get descriptionUserId(): number | null | undefined {
		return this.description?.userId;
	}

	get descriptionTimestamp(): number | undefined {
		return this.description?.modified;
	}

	get descriptionUser(): UserRecord | null {
		let user;
		if (this.descriptionUserId) {
			user = this.mmUsersService.findRecord(String(this.descriptionUserId));
			if (!user) {
				this.loggerService.logError(
					'Missing author for report slide description',
					{
						reportSlideId: this.id,
						reportId: this.reportId,
						userId: this.descriptionUserId,
					},
					Severity.WARNING,
				);
			}
		}
		return user || null;
	}

	setDescription(text: string) {
		const userModel = this.currentUserService.model;
		this.description = {
			userId: userModel?.numericId,
			userName: userModel?.name || '',
			userAvatar: userModel?.avatar || '',
			content: text,
			modified: moment().unix(),
		};
	}

	// ------- fragments ------

	_fragments: ReportSlideFragment[] = [];

	@cached
	get fragments(): ReportSlideFragment[] {
		const fragmentRecords = this.mmReportSlideFragmentsService.getRecordsForSlide(
			this.numericId!,
		);
		ModelManagerReportSlideFragmentsService.updateModelCacheHelper(
			this._fragments,
			fragmentRecords,
			this.mmReportSlideFragmentsService.createModel,
		);
		return this._fragments;
	}

	get imageFragments(): ReportSlideFragment[] {
		return this.fragments.filter((item) => item.isTypeImage);
	}

	get headerFragment(): ReportSlideFragment | null {
		return this.fragments.find((item) => item.isTypeTitle) || null;
	}

	get boardFragment(): ReportSlideFragment | null {
		return this.fragments.find((item) => item.isTypeBoard) || null;
	}

	@cached
	get previousSlide(): ReportSlide | null {
		const allSlides = this.report!.sortedSlides || [],
			allSlideIds = allSlides.map((item) => item.id),
			thisSlideIndex = allSlideIds.indexOf(this.id);
		return allSlides[thisSlideIndex - 1] || null;
	}

	@cached
	get nextSlide(): ReportSlide | null {
		const allSlides = this.report!.sortedSlides || [],
			allSlideIds = allSlides.map((item) => item.id),
			thisSlideIndex = allSlideIds.indexOf(this.id);
		return allSlides[thisSlideIndex + 1] || null;
	}

	@cached
	get fragmentsMaxRowIndex(): number {
		const rowIndices = this.fragments.map((f) => f.position.row);
		if (rowIndices.length === 0) {
			return 0;
		}

		return Math.max(...rowIndices);
	}

	@cached
	get globalFirstFragmentRow(): number {
		return this.previousSlide?.globalLastFragmentRow || 0;
	}

	@cached
	get globalLastFragmentRow(): number {
		const previousSlideGlobalLastFragmentRow = this.previousSlide?.globalLastFragmentRow || 0;
		return previousSlideGlobalLastFragmentRow + this.fragmentsMaxRowIndex;
	}

	@cached
	get hasFragmentsWithMetricData(): boolean {
		return this.fragments.some((fragment) => fragment.isFragmentWithMetricData);
	}

	getOrderFromGlobalOrder(globalOrder: number) {
		const newPosition = getPositionFromOrder(globalOrder);
		const globalFirstFragmentRow = this.globalFirstFragmentRow;
		const order = getOrderFromPosition(
			newPosition.row - globalFirstFragmentRow,
			newPosition.column,
		);
		return order;
	}

	getBlockPromises() {
		const promises: Promise<unknown>[] = [];
		this.fragments.forEach((f) => {
			// only those not loaded yet - avoid promises where data is already present
			// e.g. in snapshooter or report templates
			if (f.blockId && !f.block && !this.navigationService.isDatawallApp) {
				promises.push(this.mmBlocksService.requestSingleBlock(String(f.blockId)));
			}
		});
		return promises;
	}

	// ------- images - for snapshooter preload ------

	get allImages() {
		const images: string[] = [];
		if (this.bg) {
			images.push(this.bg);
		}
		if (this.logo) {
			images.push(this.logo);
		}
		if (this.boardOrPreview) {
			images.push(...this.boardOrPreview.allImages);
		}
		this.imageFragments.forEach((f) => {
			const url = f.value?.imageUrl;
			if (url) {
				images.push(url);
			}
		});
		return uniq(images);
	}

	override getDataForCopy(): CreateRecordProperties<ReportSlideRecord> {
		const data = this.toJSON();
		if (!this.isTypeBoard) {
			data.title = `Copy of ${data.title}`;
		}
		return data;
	}

	updateDateRange(dateRange: DateRange) {
		const requestService = this.requestService,
			data = {
				dateRange: {
					range: dateRange.rangeFormula,
				},
			},
			infolayer = this.infolayerService.loading('Saving date range...');

		requestService
			.api<RecordData.Payload>('POST', `/report-slides/${this.id}/master-range`, data)
			.then(
				(reply) => {
					this.storeService.pushPayload(reply);
					infolayer.ok('Date range saved!');
				},
				(errorData) => {
					infolayer.error('There was an error saving the date range.');
					throw errorData;
				},
			)
			.catch(requestService.showAllValidationErrors.bind(requestService));
	}

	static override modelName = ModelName.REPORT_SLIDE; // store record type
}
